import { useMemo } from "react";
import type {
  ComboBoxOption,
  ComboBoxOptions
} from "@dealer-center/core-components";
import {
  ComboBox,
  type InterstateOnChangeEvent
} from "@dealer-center/core-components";
import type {
  LocaleYearMakeData,
  LocaleYearMakeRequest,
  ModelLocale,
  SearchResponse
} from "@dealer-center/inventory-pages-gvm-interface";
import {
  comboBoxValue,
  localeComboBoxValue,
  valueFromComboBox,
  valuesToComboBoxOptions
} from "../util/comboBoxHelper";

const localesToOptions = (
  locales: ModelLocale[] | undefined
): ComboBoxOption[] => {
  if (!locales) {
    return [];
  }
  return locales.map(item => ({
    value: item.locale,
    label: item.display
  }));
};

interface LocaleYearMakeFiltersProps {
  queryData: SearchResponse<LocaleYearMakeData>;
  localeYearMakeRequest: LocaleYearMakeRequest;
  setLocaleYearMakeRequest: (data: LocaleYearMakeRequest) => void;
  resettableProps: Record<string, any>;
}

export const LocaleYearMakeFilters = ({
  queryData,
  localeYearMakeRequest,
  setLocaleYearMakeRequest,
  resettableProps
}: LocaleYearMakeFiltersProps) => {
  const isQueryError = !!queryData.error;
  const locales = useMemo(
    () => localesToOptions(queryData.data?.locales),
    [queryData.data?.locales]
  );
  const years = useMemo(
    () => valuesToComboBoxOptions(queryData.data?.years),
    [queryData.data?.years]
  );
  const makes = useMemo(
    () => valuesToComboBoxOptions(queryData.data?.makes),
    [queryData.data?.makes]
  );
  const selectedLocale = useMemo(
    () =>
      localeComboBoxValue(
        queryData.data?.locales,
        localeYearMakeRequest.locale
      ),
    [queryData.data?.locales, localeYearMakeRequest.locale]
  );
  const selectedYear = useMemo(
    () => comboBoxValue(localeYearMakeRequest.year),
    [localeYearMakeRequest.year]
  );
  const selectedMake = useMemo(
    () => comboBoxValue(localeYearMakeRequest.make),
    [localeYearMakeRequest.make]
  );

  const handleInputChange = (
    event: InterstateOnChangeEvent<ComboBoxOptions>
  ) => {
    const name = event.target.name || "";
    const selectedOption = event.target.value;

    let dependentAttr = {};

    if (name === "make") {
      dependentAttr = resettableProps;
    }

    const value = valueFromComboBox(selectedOption);

    setLocaleYearMakeRequest({
      ...localeYearMakeRequest,
      [name]: value && name === "year" ? Number(value) : value,
      ...dependentAttr
    });
  };

  return (
    <>
      <ComboBox
        name="locale"
        options={locales}
        placeholder="Select Locale"
        value={selectedLocale}
        onChange={handleInputChange}
      />
      <ComboBox
        name="year"
        options={years}
        placeholder="Select Year"
        value={selectedYear}
        onChange={handleInputChange}
      />
      <ComboBox
        name="make"
        options={makes}
        placeholder="Select Make"
        value={selectedMake}
        onChange={handleInputChange}
      />
    </>
  );
};
